import React from "react";
import { Checkbox, Skeleton } from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";
import CheckedIcon from "../../svg/CheckedIcon";
import UncheckedIcon from "../../svg/UncheckedIcon";
import ScrollableWrapper from "../common/ScrollableWrapper";
import { InbodyDateTime } from "../../../../common/network/Api";
import { getBiomarkerCategoryColor } from "../../store/helper";
import { formatDisplayDate } from "../../../../common/utils/date";

// Shape of a biomarker row returned by GetData.
type InbodyBiomarker = {
  biomarkerId: string;
  name: string;
  shortname: string;
  unit: string;
  newValue: number;
  interpretation: number;
  date: string;
  qvalue?: string | number | null;
};

type Props = {
  test: InbodyDateTime;
  synced: boolean;
  selected: boolean;
  onToggle: () => void;
  expanded: boolean;
  onToggleExpand: () => void;
  errored: boolean;
  // Prefetched by the modal: null while loading, the biomarker rows once done.
  biomarkers: InbodyBiomarker[] | null;
  loading: boolean;
};

// Display-only: round to at most 4 decimals (the stored value is untouched).
const formatValue = (raw: any) => {
  const num = Number(raw);
  if (Number.isNaN(num)) return raw;
  return Math.round(num * 10000) / 10000;
};

// Table-shaped loading placeholder for the biomarker preview.
const PreviewSkeleton = () => (
  <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
    {Array.from({ length: 5 }).map((_, row) => (
      <div key={row} style={{ display: "flex", gap: "24px" }}>
        {Array.from({ length: 3 }).map((__, col) => (
          <div key={col} style={{ flex: col === 0 ? 2 : 1 }}>
            <Skeleton
              variant="text"
              width={col === 0 ? "60%" : "50%"}
              height={20}
            />
          </div>
        ))}
      </div>
    ))}
  </div>
);

const InBodyTestCard = ({
  test,
  synced,
  selected,
  onToggle,
  expanded,
  onToggleExpand,
  errored,
  biomarkers,
  loading,
}: Props) => {
  const theme: Theme = useTheme();

  // "EQUIP" is InBody equipment metadata, not a real biomarker — hide it from
  // the list, but surface its qvalue (the InBody device) in the header.
  const isEquip = (b: InbodyBiomarker) =>
    (b.name ?? "").trim().toUpperCase() === "EQUIP";
  const visibleBiomarkers = (biomarkers ?? []).filter((b) => !isEquip(b));
  const equipQValue = (biomarkers ?? []).find(isEquip)?.qvalue;
  const biomarkerCount =
    biomarkers && !errored ? visibleBiomarkers.length : undefined;

  return (
    <div style={styles.card(theme, expanded)}>
      {/* Header row */}
      <div style={styles.headerRow}>
        <Checkbox
          icon={<UncheckedIcon fill={theme.colors_ds.NEUTRAL_ICON_WEAK} />}
          checkedIcon={
            <CheckedIcon
              fill={
                synced
                  ? theme.colorTokens.N[60]
                  : theme.colors_ds.PRIMARY_ICON_MAIN
              }
            />
          }
          checked={(selected && !errored) || synced}
          disabled={synced || errored}
          onChange={onToggle}
          disableRipple
          sx={{ padding: 0, width: "20px", height: "20px" }}
        />

        <div style={styles.titleGroup}>
          <div style={styles.dateRow}>
            <span style={styles.date(theme)}>
              {formatDisplayDate(test.date)}
            </span>
            {synced && (
              <span style={styles.syncedBadge(theme)}>
                <span style={styles.syncedDot(theme)} />
                Synced
              </span>
            )}
            {errored && !synced && (
              <span style={styles.errorBadge(theme)}>
                <span style={styles.errorDot(theme)} />
                Unable to sync
              </span>
            )}
          </div>
          <div style={styles.subtitle(theme)}>
            Full body composition
            {loading ? (
              <Skeleton
                variant="text"
                width={140}
                height={16}
                sx={{ display: "inline-block", ml: "8px", verticalAlign: "middle" }}
              />
            ) : (
              <>
                {biomarkerCount !== undefined
                  ? ` · ${biomarkerCount} biomarkers`
                  : ""}
                {equipQValue !== undefined &&
                equipQValue !== null &&
                equipQValue !== ""
                  ? ` · InBody ${equipQValue}`
                  : ""}
              </>
            )}
          </div>
        </div>

        <div onClick={onToggleExpand} style={styles.chevronWrapper}>
          <KeyboardArrowRightIcon
            style={{
              transition: "transform 0.2s ease",
              transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
              color: theme.colors_ds.NEUTRAL_ICON_DEFAULT,
            }}
          />
        </div>
      </div>

      {/* Expanded preview */}
      {expanded && (
        <div style={styles.previewContainer(theme)}>
          {loading ? (
            <PreviewSkeleton />
          ) : errored ? (
            <div style={styles.centeredMessage(theme)}>
              Something went wrong — we couldn't load the biomarkers for this
              InBody test.
            </div>
          ) : visibleBiomarkers.length > 0 ? (
            <>
              <div style={styles.previewHeaderRow(theme)}>
                <div style={styles.colName}>Name</div>
                <div style={styles.col}>Value</div>
                <div style={styles.col}>Date</div>
              </div>
              <ScrollableWrapper>
                {visibleBiomarkers.map((b, index) => (
                  <div key={index} style={styles.previewRow(theme)}>
                    <div
                      style={{ ...styles.colName, ...styles.cellName(theme) }}
                    >
                      {b.name}
                    </div>
                    <div
                      style={{
                        ...styles.col,
                        ...styles.cellValue(theme),
                        color: getBiomarkerCategoryColor(
                          b.interpretation,
                          theme,
                          "biomarkers"
                        ),
                      }}
                    >
                      {formatValue(b.newValue)} {b.unit}
                    </div>
                    <div style={{ ...styles.col, ...styles.cellMuted(theme) }}>
                      {formatDisplayDate(b.date ?? test.date)}
                    </div>
                  </div>
                ))}
              </ScrollableWrapper>
            </>
          ) : (
            <div style={styles.centeredMessage(theme)}>
              No biomarkers found.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default InBodyTestCard;

const styles = {
  card: (theme: Theme, expanded: boolean) => ({
    border: `1px solid ${theme.colorTokens.N[50]}`,
    borderRadius: "8px",
    backgroundColor: expanded
      ? theme.colorTokens.N[30]
      : theme.colors_ds.NEUTRAL_BG_MAIN,
    overflow: "hidden" as "hidden",
    display: "flex",
    flexDirection: "column" as "column",
    // Expanded cards grow to fill (and share) the available height; collapsed
    // cards keep their natural height.
    ...(expanded
      ? { flex: 1, minHeight: 0 }
      : { flexShrink: 0 as 0 }),
  }),
  headerRow: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "16px",
    flexShrink: 0 as 0,
  },
  titleGroup: {
    display: "flex",
    flexDirection: "column" as "column",
    gap: "4px",
    flex: 1,
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    // Reserve the badge height so the card height stays constant whether or not
    // a Synced / Unable-to-sync badge is shown.
    minHeight: "26px",
  },
  date: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "16px",
    lineHeight: "22px",
    color: theme.colorTokens.N[100],
  }),
  subtitle: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    lineHeight: "17px",
    color: theme.colorTokens.N[80],
  }),
  syncedBadge: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "2px 8px",
    borderRadius: "8px",
    border: `1px solid ${theme.colorTokens.S[70]}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colorTokens.N[100],
  }),
  syncedDot: (theme: Theme) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.S[70],
  }),
  errorBadge: (theme: Theme) => ({
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "2px 8px",
    borderRadius: "8px",
    border: `1px solid ${theme.colorTokens.D[70]}`,
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colorTokens.N[100],
  }),
  errorDot: (theme: Theme) => ({
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    backgroundColor: theme.colorTokens.D[70],
  }),
  chevronWrapper: {
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
  },
  previewContainer: (theme: Theme) => ({
    borderTop: `1px solid ${theme.colorTokens.N[50]}`,
    padding: "8px 16px 16px 16px",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    // Fill the card's remaining height so the rows scroll internally.
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as "column",
  }),
  previewHeaderRow: (theme: Theme) => ({
    display: "flex",
    gap: "24px",
    padding: "12px 0",
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    color: theme.colorTokens.N[80],
    flexShrink: 0 as 0,
  }),
  previewRow: (theme: Theme) => ({
    display: "flex",
    gap: "24px",
    padding: "12px 0",
    borderTop: `1px solid ${theme.colorTokens.N[40]}`,
    alignItems: "center",
  }),
  col: {
    flex: 1,
  },
  colName: {
    flex: 2,
  },
  cellName: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colorTokens.N[90],
  }),
  cellValue: (theme: Theme) => ({
    fontFamily: "var(--font-Bold)",
    fontSize: "14px",
  }),
  cellMuted: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colorTokens.N[80],
  }),
  centeredMessage: (theme: Theme) => ({
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center" as "center",
    padding: "16px",
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    color: theme.colorTokens.N[80],
  }),
};
